import { useState } from 'react';
import classNames from 'classnames';
import uuid from 'react-uuid';
import Icon from '../Icon';

type Props = {
  id?: string;
  name?: string;
  defaultChecked?: boolean;
  theme?: 'light' | 'dark';
  reverse?: boolean;
  fluid?: boolean;
  disabled?: boolean;
  className?: string;
  children?:
    | string
    | string[]
    | JSX.Element
    | JSX.Element[];
};

function Checkbox({
  id,
  name = '',
  defaultChecked = false,
  theme = 'dark',
  reverse = false,
  fluid = false,
  disabled = false,
  className,
  children
}: Props) {
  const [isChecked, setIsChecked] =
    useState<boolean>(defaultChecked);

  const generatedId = uuid();

  const handleChange = (e: React.BaseSyntheticEvent) => {
    setIsChecked(e.target.checked);
  };

  const getCheckboxColor = (color: string) => {
    switch (color) {
      case 'light':
        return 'bg-white bg-opacity-25 peer-checked:bg-sl-indigo peer-checked:bg-opacity-100';
      case 'dark':
        return 'bg-sl-blue bg-opacity-10 peer-checked:bg-opacity-100';
      default:
        return '';
    }
  };
  return (
    <label
      htmlFor={id || generatedId}
      className={classNames([
        className,
        'items-center gap-x-3 font-camphor text-sm leading-tight md:text-base lg:text-lg',
        reverse && 'flex-row-reverse',
        fluid ? 'flex' : 'inline-flex'
      ])}
    >
      <input
        type="checkbox"
        name={name}
        id={id || generatedId}
        disabled={disabled}
        checked={isChecked}
        onChange={(e) => handleChange(e)}
        className="peer hidden"
      />
      <span
        className={classNames([
          'inline-flex h-7 w-7 shrink-0 basis-7 items-center justify-center self-start rounded-[4px] transition-colors peer-disabled:cursor-not-allowed',
          getCheckboxColor(theme)
        ])}
      >
        <Icon
          icon="checkmark"
          className={classNames([
            'text-lg leading-none text-white transition-all',
            isChecked
              ? 'scale-100 opacity-100'
              : 'scale-0 opacity-0'
          ])}
        />
      </span>
      <span
        className={classNames([
          'flex-1 select-none transition-colors',
          theme === 'light' && 'text-white',
          theme === 'dark' && 'text-sl-blue',
          !isChecked && ' text-opacity-50',
          isChecked && ' text-opacity-100'
        ])}
      >
        {children && children}
      </span>
    </label>
  );
}

export default Checkbox;
